/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { EndeavorApi, DocumentApi } from '../services/ApiService';
import './MobileDocumentSelectionModal.css';
import MobileMyDocumentsCard from './components/MobileMyDocumentsCard';
import { getAudioDocumentTypeIds } from '../utils/documentTypeUtils';

const MobileDocumentSelectionModal = (props) => {
  const { 
    isOpen, 
    onClose, 
    onConnectDocuments, 
    selectedDocuments = [], 
    onToggleDocument,
    requiredStatusIds = [],
    documentTypeId = null,
    title = "Select Documents",
    description = "Choose processed documents to ground the AI responses."
  } = props;

  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [endeavors, setEndeavors] = useState([]);
  const [selectedEndeavor, setSelectedEndeavor] = useState('all');
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [statusFilterInfo, setStatusFilterInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortOption, setSortOption] = useState('date');
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const { user } = useAuth();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Move this ABOVE effects that use it
  const getFilteredDocuments = useCallback(() => {
    let filtered = availableDocuments;

    if (selectedEndeavor !== 'all') {
      filtered = filtered.filter(doc => {
        const docEndeavorId = (doc.endeavorId || doc.endeavor_id || '').toString();
        return docEndeavorId === selectedEndeavor.toString();
      });
    }

    if (debouncedSearch.trim()) {
      const searchTerm = debouncedSearch.toLowerCase();
      filtered = filtered.filter(doc =>
        (doc.name || '').toLowerCase().includes(searchTerm) ||
        (doc.endeavorName || '').toLowerCase().includes(searchTerm)
      );
    }

    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'course':
          return (a.endeavorName || 'No Course').localeCompare(b.endeavorName || 'No Course');
        case 'date':
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });

    return filtered;
  }, [availableDocuments, selectedEndeavor, debouncedSearch, sortOption]);

  const fetchDocumentsAndEndeavors = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const userId = user?.id || user?.userId;
      
      if (!userId) {
        console.error('MobileDocumentSelectionModal: No user ID available');
        return;
      }

      let userDocuments = [];

      if (documentTypeId) {
        const documentsResponse = await DocumentApi.getDocumentsByUserAndType(userId, documentTypeId);
        
        if (!documentsResponse.ok) {
          const fallbackResponse = await DocumentApi.getDocumentsByType(documentTypeId);
          if (fallbackResponse.ok) {
            const allDocuments = await fallbackResponse.json();
            userDocuments = Array.isArray(allDocuments) 
              ? allDocuments.filter(doc => doc.userId === userId || doc.user_id === userId)
              : [];
          }
        } else {
          const data = await documentsResponse.json();
          userDocuments = Array.isArray(data) ? data : [];
        }
      } else {
        const audioTypeIds = await getAudioDocumentTypeIds();
        const documentsResponse = await DocumentApi.getDocumentsByUser(userId, {
          excludeDocumentTypeIds: audioTypeIds
        });
        
        if (!documentsResponse.ok) {
          throw new Error(`Failed to fetch documents: ${documentsResponse.status}`);
        }
        
        const data = await documentsResponse.json();
        userDocuments = Array.isArray(data) ? data : (data.content || []);
      }

      // NOTE: Status filtering removed to show ALL documents (including unprocessed).
      // UnifiedContentService on backend now handles unprocessed documents transparently.
      // Instead of hiding unprocessed docs, we show them all and the backend will
      // use full-text extraction for those without RAG processing.
      let processedDocuments = userDocuments;
      
      // Mark documents without required status as unprocessed for potential UI indication
      if (requiredStatusIds.length > 0) {
        processedDocuments = userDocuments.map(doc => {
          if (!doc.statuses || !Array.isArray(doc.statuses)) {
            return { ...doc, _isUnprocessed: true };
          }
          
          const hasRequiredStatus = requiredStatusIds.some(requiredId => 
            doc.statuses.some(status => {
              const statusId = status.statusId || status.status_id || status.id;
              return statusId === requiredId || statusId === requiredId.toString();
            })
          );
          
          return { ...doc, _isUnprocessed: !hasRequiredStatus };
        });

        // Update status filter info to show all documents
        const statusNames = [];
        if (requiredStatusIds.includes(4) || requiredStatusIds.includes('4')) {
          statusNames.push('RAG Processed');
        }
        if (requiredStatusIds.includes(5) || requiredStatusIds.includes('5')) {
          statusNames.push('Metadata Tags Processed');
        }
        
        const unprocessedCount = processedDocuments.filter(d => d._isUnprocessed).length;
        
        setStatusFilterInfo({
          requiredStatusNames: statusNames.length > 0 ? statusNames : [`Status ${requiredStatusIds.join(', ')}`],
          totalDocuments: userDocuments.length,
          availableDocuments: userDocuments.length,
          filteredOutDocuments: 0,
          unprocessedCount: unprocessedCount
        });
      } else {
        setStatusFilterInfo(null);
      }

      setAvailableDocuments(processedDocuments);

      // Fetch endeavors
      try {
        const endeavorsResponse = await EndeavorApi.getEndeavorsByUser(userId);
        
        if (!endeavorsResponse.ok) {
          throw new Error(`Failed to fetch endeavors: ${endeavorsResponse.status}`);
        }
        
        const endeavorsData = await endeavorsResponse.json();
        const userEndeavors = Array.isArray(endeavorsData) ? endeavorsData : (endeavorsData.content || []);
        
        const endeavorsList = userEndeavors.map(endeavor => ({
          id: endeavor.endeavorId || endeavor.id,
          name: endeavor.name || endeavor.title || 'Unnamed Endeavor'
        }));

        endeavorsList.sort((a, b) => a.name.localeCompare(b.name));
        setEndeavors(endeavorsList);

        // Enrich documents with endeavor names
        setAvailableDocuments(prevDocs => {
          if (!prevDocs || prevDocs.length === 0) return prevDocs;
          return prevDocs.map(d => {
            if (d.endeavorName && d.endeavorName !== 'Unassigned') return d;
            const docEndeavorId = (d.endeavorId || d.endeavor_id || '').toString();
            const match = endeavorsList.find(e => e.id?.toString() === docEndeavorId);
            if (match) return { ...d, endeavorName: match.name };
            return d;
          });
        });
      } catch (error) {
        console.error('MobileDocumentSelectionModal: Error fetching endeavors:', error);
        setEndeavors([]);
      }
    } catch (error) {
      console.error('MobileDocumentSelectionModal: Error fetching documents:', error);
      setAvailableDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, [user, requiredStatusIds, documentTypeId]);

  useEffect(() => {
    if (isOpen) {
      fetchDocumentsAndEndeavors();
    }
  }, [isOpen, fetchDocumentsAndEndeavors]);

  // Update select all checkbox state (now after getFilteredDocuments)
  useEffect(() => {
    const filteredDocs = getFilteredDocuments();
    if (filteredDocs.length === 0) {
      setSelectAllChecked(false);
    } else {
      setSelectAllChecked(filteredDocs.every(doc => selectedDocuments.includes(doc.id)));
    }
  }, [selectedDocuments, getFilteredDocuments]); // include memoized function

  const handleSelectAll = () => {
    const filteredDocs = getFilteredDocuments();
    const allSelected = filteredDocs.every(doc => selectedDocuments.includes(doc.id));
    
    if (allSelected) {
      // Deselect all filtered documents
      filteredDocs.forEach(doc => {
        if (selectedDocuments.includes(doc.id)) {
          onToggleDocument(doc.id);
        }
      });
    } else {
      // Select all filtered documents
      filteredDocs.forEach(doc => {
        if (!selectedDocuments.includes(doc.id)) {
          onToggleDocument(doc.id);
        }
      });
    }
  };

  // Lock background scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConnect = () => {
    if (selectedDocuments.length > 0) {
      onConnectDocuments(selectedDocuments.map(String));
      onClose();
    }
  };

  return (
    <div className="mobile-document-modal-overlay" onClick={onClose}>
      <div className="mobile-document-selection-modal" onClick={(e)=>e.stopPropagation()}>
        <div className="mobile-modal-header">
          <div className="mobile-modal-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" fill="#6366f1"/>
            </svg>
            <h3>{title}</h3>
          </div>
          <button className="mobile-close-button" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mobile-modal-description">
          <p>{description}</p>
        </div>

        {/* Drop in the mobile documents card (mirrors desktop behavior) */}
        <div className="mobile-documents-section">
          <MobileMyDocumentsCard
            selectionMode
            selectedDocumentIds={selectedDocuments.map(String)}
            onToggleSelect={(id) => onToggleDocument?.(String(id))}
            requiredStatusIds={requiredStatusIds}
            pinnedDocumentTypeId={documentTypeId || null}
          />
        </div>

        <div className="mobile-modal-footer">
          <button 
            className="mobile-connect-button" 
            onClick={handleConnect}
            disabled={selectedDocuments.length === 0}
          >
            Connect ({selectedDocuments.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileDocumentSelectionModal;
